import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  getRates,
  confirmShipment,
  cancelShipment,
  getLabelContextFromQuote,
  getQuoteShipments,
  getPackagePresets,
  createPackagePreset,
  updatePackagePreset,
  deletePackagePreset,
  getPickupInstructions,
  getEligiblePickups,
} from '../api';
import { pcKeys } from './queryKeys';
import type {
  RatesRequest,
  ConfirmShipmentInput,
  PackagePreset,
  PackagePresetInput,
} from '../types';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// The backend returns length/width/height/defaultWeight as strings for
// package presets (node-postgres serializes NUMERIC/DECIMAL columns as
// strings) even though the TS type declares them as `number | null`.
// Coerce them to real numbers so consumers never have to guess.
function toNumberOrNull(value: number | string | null): number | null {
  return value == null ? null : Number(value);
}

function normalizePackagePreset(preset: PackagePreset): PackagePreset {
  return {
    ...preset,
    length: toNumberOrNull(preset.length),
    width: toNumberOrNull(preset.width),
    height: toNumberOrNull(preset.height),
    defaultWeight: toNumberOrNull(preset.defaultWeight),
  };
}

// ── Rates / Confirm Shipment (draft-first labels) ──

export function useCalculateRates() {
  const { t } = useTranslation('planete-courrier');
  return useMutation({
    mutationFn: (req: RatesRequest) => getRates(req),
    onError: (error) => {
      toast.error(t('errors.ratesFailed'), { description: getErrorMessage(error) });
    },
  });
}

export function useConfirmShipment() {
  const { t } = useTranslation('planete-courrier');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConfirmShipmentInput) => confirmShipment(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pcKeys.shipments() });
      toast.success(t('labelFlow.created'));
    },
    onError: (error) => {
      const raw = getErrorMessage(error);
      // Map the known Galaxie/backend pickup-window messages to friendly copy;
      // fall back to the raw server message for everything else.
      let description = raw;
      if (/later or equals to now|must be in the future/i.test(raw)) {
        description = t('errors.pickupPast', 'The pickup start time is in the past. Choose a later time or a future date.');
      } else if (/more than 7 days|within the next 7 days/i.test(raw)) {
        description = t('errors.pickupTooFar', 'The pickup must be scheduled within the next 7 days.');
      } else if (/pickup location/i.test(raw)) {
        // Covers both our own pre-flight guard and the raw vendor blob
        // ("PurolatorPickUp ... '4100113': The Pickup Location is a mandatory field.")
        // so a JSON payload never reaches the operator again.
        description = t('errors.pickupLocationMissing', 'The carrier needs a pickup location for this shipment. Choose one and try again.');
      }
      toast.error(t('errors.createFailed'), { description });
    },
  });
}

// The carrier's legal pickup locations, for the label modal's picker. Gated on
// BOTH a known carrier and a scheduled pickup, so no lookup fires for a label
// that has no pickup. The lists are effectively static, hence the long staleTime.
export function usePickupInstructions(carrierCode: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: pcKeys.pickupInstructions(carrierCode ?? ''),
    queryFn: () => getPickupInstructions(carrierCode as string),
    enabled: Boolean(carrierCode) && enabled,
    staleTime: 24 * 60 * 60 * 1000,
  });
}

// Pickup requests this draft can ATTACH to (one driver stop for several labels).
// Scoped to the chosen service agreement, so the caller re-queries when the rate
// selection changes. Best-effort convenience: a failed lookup only hides the
// attach option — it must never block the flow — so no retries and no error toast.
export function useEligiblePickups(
  draftId: number | null,
  serviceAgreementId: number | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: pcKeys.eligiblePickups(draftId, serviceAgreementId),
    queryFn: () => getEligiblePickups(draftId as number, serviceAgreementId as number),
    enabled: enabled && draftId != null && serviceAgreementId != null,
    // The list changes as pickups are scheduled and collected — keep it short-lived.
    staleTime: 30 * 1000,
    retry: false,
  });
}

export function useCancelShipment() {
  const { t } = useTranslation('planete-courrier');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ number, cancellationReason }: { number: string; cancellationReason?: string }) =>
      cancelShipment(number, cancellationReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pcKeys.shipments() });
      toast.success(t('labelFlow.cancelled'));
    },
    onError: (error) => {
      toast.error(t('errors.cancelFailed'), { description: getErrorMessage(error) });
    },
  });
}

export function useLabelContext() {
  const { t } = useTranslation('planete-courrier');
  return useMutation({
    mutationFn: (quoteId: string) => getLabelContextFromQuote(quoteId),
    onError: (error) => {
      toast.error(t('errors.prefillFailed'), { description: getErrorMessage(error) });
    },
  });
}

// Shipping labels already recorded against a quote — drives the "a label already
// exists" warning and the per-quote label list. Auto-refreshes on confirm/cancel
// (its key is nested under pcKeys.shipments()).
export function useQuoteShipments(quoteId: string | undefined) {
  return useQuery({
    queryKey: pcKeys.quoteShipments(quoteId ?? ''),
    queryFn: () => getQuoteShipments(quoteId as string),
    enabled: !!quoteId,
  });
}

// ── Package presets ──

export function usePackagePresets() {
  return useQuery({
    queryKey: pcKeys.packagePresets(),
    queryFn: getPackagePresets,
    select: (presets) => presets.map(normalizePackagePreset),
  });
}

export function useCreatePackagePreset() {
  const { t } = useTranslation('planete-courrier');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PackagePresetInput) => createPackagePreset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pcKeys.packagePresets() });
      toast.success(t('presets.saved'));
    },
    onError: (error) => {
      toast.error(t('errors.presetSaveFailed'), { description: getErrorMessage(error) });
    },
  });
}

export function useUpdatePackagePreset() {
  const { t } = useTranslation('planete-courrier');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PackagePresetInput> }) =>
      updatePackagePreset(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pcKeys.packagePresets() });
      toast.success(t('presets.saved'));
    },
    onError: (error) => {
      toast.error(t('errors.presetSaveFailed'), { description: getErrorMessage(error) });
    },
  });
}

export function useDeletePackagePreset() {
  const { t } = useTranslation('planete-courrier');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePackagePreset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pcKeys.packagePresets() });
      toast.success(t('presets.deleted'));
    },
    onError: (error) => {
      toast.error(t('errors.presetDeleteFailed'), { description: getErrorMessage(error) });
    },
  });
}
